import { useEffect, useState, useRef } from 'react'
import {
    ScoopButton,
    ScoopSpinner,
    ScoopChip,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
    ScoopText,
} from '../../../ui'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useUserInfo } from '../../../../api/hooks/user/useUserInfo'
import { useOnboarding } from '../../../../api/hooks/onboarding/useOnboarding'
import { WelcomeStep } from './WelcomeStep/WelcomeStep'
import { DatasetStep } from './DatasetStep/DatasetStep'
import { switchWorkspace } from '../../../../store/actions/authActions'
import {
    POLLING_CHECK_ACTIONS,
    useProcessingPolling,
} from '../../../../api/hooks/processing/useProcessingPolling'
import { ROUTES } from '../../../../router/routes'
import { useRecipes } from '../../../../api/hooks/recipes/useRecipes'
import { useWorkspaceMetadata } from '../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { SideBanner } from './SideBanner/SideBanner'
import { useWorkspaces } from '../../../../api/hooks/workspaces/useWorkspaces'
import { ModalProvider, ModalOutlet } from '../../../modals'
import styles from './Questionnaire.module.scss'

// Unified step configuration
const STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to Scoop',
        description:
            'Scoop connects to your business tools and turns your data into actionable insights — no setup, no code, no hassle.',
        component: WelcomeStep,
        buttonText: 'Get Started',
        validate: () => true,
    },
    {
        id: 'dataset',
        title: 'Connect Your Data',
        description: 'Choose your source to see insights instantly.',
        hasClickableDescription: true,
        component: DatasetStep,
        validate: () => false, // This step has its own navigation logic
    },
]

export const Questionnaire = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()

    // Check for pending invitation and redirect to accept-invitation if found
    useEffect(() => {
        const pendingInvitation = localStorage.getItem('pendingInvitation')
        if (pendingInvitation) {
            // Redirect to accept-invitation page, which will redirect back after acceptance
            navigate(`${ROUTES.ACCEPT_INVITATION}?token=${pendingInvitation}&returnTo=onboarding`)
        }
    }, [navigate])

    // Get initial step index from location state or default to 0
    const initialStepIndex = location?.state?.stepIndex || 0
    const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex)
    const currentStep = STEPS[currentStepIndex]

    // Track if user came directly to a specific step (e.g., from banner)
    const [cameWithStepIndex] = useState(!!location?.state?.stepIndex)
    // Track if user came from Tour workspace to upload data (stepIndex: 1)
    const [cameFromTourToUpload] = useState(location?.state?.stepIndex === 1)

    // Form state
    const [createdInbox, setCreatedInbox] = useState(null)
    const [processingPolling, setProcessingPolling] = useState(false)
    const [
        processingPollingIsTakingMoreThanExpected,
        setProcessingPollingIsTakingMoreThanExpected,
    ] = useState(false)
    const [reachedMaxPolling, setReachedMaxPolling] = useState(false)

    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
    const scoopUserEmail = useSelector((state) => state.auth.userEmail)
    const auth = useSelector((state) => state.auth)

    const { refetch, workspaceMetadata, isLoading: isLoadingMetadata } = useWorkspaceMetadata()
    const { workspaces, isLoadingWorkspaces } = useWorkspaces()
    const { user, loadingUser } = useUserInfo()
    const { reset: resetOnboarding } = useOnboarding()

    useProcessingPolling(
        POLLING_CHECK_ACTIONS.PROCESSING,
        processingPolling,
        setProcessingPolling,
        createdInbox,
        (data) => {
            if (data?.error === 'Request timed out') {
                setReachedMaxPolling(true)
            } else {
                resetOnboarding()
                refetch()
                navigate(ROUTES.INSIGHTS, {
                    state: {
                        createdInbox,
                        showOnboardingPrompts: true,
                    },
                })
            }
        },
        60, // Max polling attempts (60 * 2 seconds = 2 minutes)
        () => setProcessingPollingIsTakingMoreThanExpected(true),
        auth.workspaceID
    )
    const { recipes, isLoading: isLoadingRecipes } = useRecipes()
    const identityAlreadySet = useRef(false)

    const userID = user?.userID

    useEffect(() => {
        if (createdInbox) setProcessingPolling(true)
    }, [createdInbox])

    // Switch to personal workspace only for full onboarding flow
    // Don't switch when coming from Tour to upload data - user should stay on Tour visually
    useEffect(() => {
        if (user?.personalWorkspace && !cameFromTourToUpload) {
            dispatch(switchWorkspace(user.personalWorkspace, user.firstName + "'s Workspace"))
        }
    }, [user, dispatch, cameFromTourToUpload])

    useEffect(() => {
        if (!isAuthenticated) navigate(ROUTES.LOGIN)
    }, [isAuthenticated])

    // push GA for Lexi to make sure we capture user if they bail out of the questionnaire
    useEffect(() => {
        if (scoopUserEmail && userID && !identityAlreadySet.current) {
            identityAlreadySet.current = true

            try {
                window.dataLayer?.push({
                    event: 'email_verified',
                    enhanced_conversion_data: {
                        email: scoopUserEmail,
                        userScoopID: userID,
                    },
                })
                window.dataLayer?.push({
                    event: 'userScoopID',
                    enhanced_conversion_data_userId: {
                        userScoopID: userID,
                    },
                })
            } catch (error) {
                console.error('Error pushing data to dataLayer:', error)
            }
        }
    }, [scoopUserEmail, userID])

    const handleNext = () => {
        setCurrentStepIndex(currentStepIndex + 1)
    }

    const isNextEnabled = () => {
        return currentStep.validate ? currentStep.validate() : true
    }

    const renderStep = () => {
        switch (currentStep?.id) {
            case 'welcome':
                return (
                    <WelcomeStep title={currentStep.title} description={currentStep.description} />
                )
            case 'dataset': {
                // Find Tour workspace for switching back if user cancels
                const tourWs = cameFromTourToUpload
                    ? workspaces?.find(
                          (w) => w.workspaceID === user?.defaultTour || w.name === 'Tour Scoop'
                      )
                    : null
                return (
                    <DatasetStep
                        title={currentStep.title}
                        description={currentStep.description}
                        processingPolling={processingPolling}
                        processingPollingIsTakingMoreThanExpected={
                            processingPollingIsTakingMoreThanExpected
                        }
                        reachedMaxPolling={reachedMaxPolling}
                        inbox={createdInbox}
                        setCreatedInbox={setCreatedInbox}
                        step={currentStepIndex}
                        setStep={setCurrentStepIndex}
                        personalWorkspace={
                            cameFromTourToUpload && user?.personalWorkspace
                                ? {
                                      id: user.personalWorkspace,
                                      name: user.firstName + "'s Workspace",
                                  }
                                : null
                        }
                        tourWorkspace={
                            tourWs
                                ? {
                                      id: tourWs.workspaceID,
                                      name: tourWs.name,
                                  }
                                : null
                        }
                    />
                )
            }
            default:
                return null
        }
    }

    const getNextButtonContent = () => {
        return currentStep.buttonText || 'Next'
    }

    if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} />

    // Check if all critical data is loading
    const isLoadingCriticalData =
        loadingUser || isLoadingWorkspaces || isLoadingMetadata || isLoadingRecipes

    // Show loading state while fetching critical data
    if (isLoadingCriticalData) {
        return (
            <ScoopCenter className={styles.loadingContainer}>
                <ScoopSpinner size={60} />
                <ScoopText className={styles.loadingText}>Loading...</ScoopText>
            </ScoopCenter>
        )
    }

    // Check if user has datasets in any workspace except Tour Scoop
    const currentWorkspaceID = auth.workspaceID
    const isInTourWorkspace = workspaces?.some(
        (w) => w.workspaceID === currentWorkspaceID && w.name === 'Tour Scoop'
    )

    // If user is in Tour Scoop, switch to their personal workspace to check properly
    // BUT only if they're not coming from Tour to upload data (stepIndex: 1)
    if (isInTourWorkspace && user?.personalWorkspace && !cameFromTourToUpload) {
        dispatch(switchWorkspace(user.personalWorkspace, user.firstName + "'s Workspace"))
    }

    // Only redirect if user has datasets in their personal workspace (after switching from Tour)
    // BUT only check this on the first step
    if (
        currentStepIndex === 0 &&
        !isInTourWorkspace &&
        workspaceMetadata?.inboxes &&
        workspaceMetadata.inboxes.length > 0
    ) {
        return <Navigate to={ROUTES.INSIGHTS} />
    }

    return (
        <ModalProvider>
            <ScoopRow className={styles.container}>
                <SideBanner />
                <ScoopColumn className={styles.mainContent}>
                    <ScoopColumn className={styles.contentColumn}>
                        <ScoopColumn className={styles.stepContainer}>
                            {/* STEP CHIP */}
                            <ScoopCenter className={styles.chipCenter}>
                                <ScoopChip
                                    size="medium"
                                    label={`Step ${currentStepIndex + 1} of ${STEPS.length}`}
                                    className={styles.stepChip}
                                />
                            </ScoopCenter>
                            {/* CONTENT */}
                            <div className={styles.stepContent}>{renderStep()}</div>
                        </ScoopColumn>
                        {/* NAVIGATION BUTTONS */}
                        <ScoopRow className={styles.navigationRow}>
                            {!processingPolling ? (
                                <ScoopText
                                    className={styles.backText}
                                    onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
                                >
                                    {currentStepIndex === 0 ||
                                    (cameWithStepIndex && currentStep.id === 'dataset')
                                        ? ''
                                        : 'Back'}
                                </ScoopText>
                            ) : (
                                <div />
                            )}
                            <ScoopButton disabled={!isNextEnabled()} onClick={handleNext}>
                                {getNextButtonContent()}
                            </ScoopButton>
                        </ScoopRow>
                    </ScoopColumn>
                </ScoopColumn>
            </ScoopRow>
            <ModalOutlet />
        </ModalProvider>
    )
}
